// The visit's signatures (#502): the client's, with their name, and the
// technician's, drawn with a finger or pencil and saved to the job's site
// (needs signal). A contract can require them before the visit is completed.
import { useRef, useState, type PointerEvent } from 'react';
import { Button, Label } from '@higgins/ui';
import { ApiError, isConnectionFailure } from '../lib/api';
import type { DeviceRecord } from '../lib/device-db';
import { signVisit } from '../lib/visit-lifecycle';
import { localDay } from '../lib/visit-steps';
import { SavedTextField } from './saved-text-field';

const PAD_WIDTH = 600;
const PAD_HEIGHT = 200;
const STROKE = 2.5;
const NAME_MAX = 200;

/** One signature box: draw, Clear, Save. A saved signature stays in the box (#616) until it is cleared or drawn over. */
function Pad({ label, onSave, disabled }: { label: string; onSave: (png: Blob) => Promise<void>; disabled: boolean }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const [drawn, setDrawn] = useState(false);
  const [saved, setSaved] = useState(false);

  const point = (e: PointerEvent<HTMLCanvasElement>) => {
    const box = e.currentTarget.getBoundingClientRect();
    return { x: ((e.clientX - box.left) / box.width) * PAD_WIDTH, y: ((e.clientY - box.top) / box.height) * PAD_HEIGHT };
  };

  function down(e: PointerEvent<HTMLCanvasElement>) {
    const ctx = e.currentTarget.getContext('2d');
    if (!ctx || disabled) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    drawing.current = true;
    const { x, y } = point(e);
    ctx.lineWidth = STROKE;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#111827';
    ctx.beginPath();
    ctx.moveTo(x, y);
  }

  function move(e: PointerEvent<HTMLCanvasElement>) {
    const ctx = e.currentTarget.getContext('2d');
    if (!drawing.current || !ctx) return;
    const { x, y } = point(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setDrawn(true);
    setSaved(false);
  }

  function clear() {
    canvas.current?.getContext('2d')?.clearRect(0, 0, PAD_WIDTH, PAD_HEIGHT);
    setDrawn(false);
    setSaved(false);
  }

  function save() {
    canvas.current?.toBlob((png) => png && void onSave(png).then(() => setSaved(true), () => undefined), 'image/png');
  }

  return (
    <div>
      <Label className="text-xs text-slate-600">{label}</Label>
      <canvas
        ref={canvas}
        width={PAD_WIDTH}
        height={PAD_HEIGHT}
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={() => (drawing.current = false)}
        onPointerCancel={() => (drawing.current = false)}
        className="mt-1 aspect-[3/1] w-full touch-none rounded-md border border-slate-300 bg-white"
        aria-label={`${label}: sign here`}
      />
      <div className="mt-1 flex gap-2">
        <Button size="sm" className="min-h-11" disabled={!drawn || saved || disabled} onClick={save}>
          {saved ? 'Saved' : 'Save signature'}
        </Button>
        <Button variant="outline" size="sm" className="min-h-11" disabled={!drawn || disabled} onClick={clear}>
          Clear
        </Button>
      </div>
    </div>
  );
}

function describe(err: unknown): string {
  if (isConnectionFailure(err)) return 'Saving a signature needs signal.';
  if (err instanceof ApiError) return err.message;
  return 'The signature wasn’t saved. Try again.';
}

export function VisitSignatures({ visit, locked }: { visit: DeviceRecord; locked: boolean }) {
  const [name, setName] = useState(typeof visit.fields.hig_clientsignatoryname === 'string' ? visit.fields.hig_clientsignatoryname : '');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function save(who: 'client' | 'technician', png: Blob) {
    setBusy(true);
    setProblem(null);
    try {
      await signVisit(visit, who, name, png);
    } catch (err) {
      setProblem(describe(err));
      throw err;
    } finally {
      setBusy(false);
    }
  }

  const signed = (column: string) => (typeof visit.fields[column] === 'string' ? `Signed ${localDay(visit.fields[column])}.` : 'Not signed yet.');

  return (
    <div className="space-y-3">
      <div>
        <Label>Signatures</Label>
        <p className="text-xs text-slate-500">Some clients&rsquo; contracts need them before the visit can be completed. Signing again replaces the earlier one.</p>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="signatory-name" className="text-xs text-slate-600">Client&rsquo;s name</Label>
          <SavedTextField id="signatory-name" label="Client’s name" value={name} onSave={setName} maxLength={NAME_MAX} />
          <Pad label="Client’s signature" disabled={busy || locked || !name.trim()} onSave={(png) => save('client', png)} />
          <p className="text-xs text-slate-500">
            {signed('hig_clientsignedon')} {typeof visit.fields.hig_clientsignatoryname === 'string' ? `(${visit.fields.hig_clientsignatoryname})` : ''}
          </p>
        </div>
        <div className="space-y-2">
          <Pad label="Technician’s signature" disabled={busy || locked} onSave={(png) => save('technician', png)} />
          <p className="text-xs text-slate-500">{signed('hig_techniciansignedon')}</p>
        </div>
      </div>
      {problem && <p className="text-sm text-red-700">{problem}</p>}
    </div>
  );
}
